// Sprint 9.5 — Analytics PRO dashboard (FULL production).
//
// Sezioni:
//   1. Range picker + comparison delta vs periodo precedente
//   2. KPI grid (4 card): pazienti, aderenza media, attivi, conversion
//   3. RetentionHeatmap (cohort × D7/D30/D90)
//   4. AdherenceBars + drill-down modale per bucket
//   5. Weight target tracking (donut + reaching list)
//   6. FunnelChart + drill-down step
//   7. Export multi-formato (CSV/JSON/HTML→PDF)
//   8. Saved views + scheduled reports

import { useState, useMemo } from 'react';
import {
  Users, Activity, ClipboardCheck, TrendingUp, Download, Mail, Settings, X, MessageSquare, RefreshCw,
} from 'lucide-react';
import { ProTopBar } from '../components/ProTopBar';
import { AnalyticsKPICard } from '../components/AnalyticsKPICard';
import { RetentionHeatmap } from '../components/RetentionHeatmap';
import { AdherenceBars } from '../components/AdherenceBars';
import { FunnelChart } from '../components/FunnelChart';
import {
  useAnalyticsOverview,
  useAnalyticsRetention,
  useAnalyticsAdherence,
  useAnalyticsAdherenceBucket,
  useAnalyticsWeightTarget,
  useAnalyticsFunnel,
  useAnalyticsFunnelStep,
  useAnalyticsScheduledReports,
  useCreateAnalyticsScheduledReport,
  useDeleteAnalyticsScheduledReport,
  useAnalyticsSavedViews,
  useCreateAnalyticsSavedView,
} from '../lib/queries';
import { AnalyticsRange, FunnelStep, exportAnalyticsUrl } from '../lib/api';
import { useQueryClient } from '@tanstack/react-query';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatNumber(n: number): string {
  return n.toLocaleString('it-IT');
}

function rangeLabel(r: AnalyticsRange, from?: string | null, to?: string | null): string {
  switch (r) {
    case '7d': return '7 giorni';
    case '30d': return '30 giorni';
    case '90d': return '90 giorni';
    case 'q': return 'Trimestre';
    case 'y': return 'Anno';
    case 'custom': return from && to ? `${from} → ${to}` : 'Personalizzato';
    default: return '30 giorni';
  }
}

// ---------------------------------------------------------------------------
// Range picker
// ---------------------------------------------------------------------------

interface RangePickerProps {
  range: AnalyticsRange;
  customFrom: string | null;
  customTo: string | null;
  onChange: (r: AnalyticsRange, from: string | null, to: string | null) => void;
}

function RangePicker({ range, customFrom, customTo, onChange }: RangePickerProps) {
  const options: Array<{ key: AnalyticsRange; label: string }> = [
    { key: '7d', label: '7gg' },
    { key: '30d', label: '30gg' },
    { key: '90d', label: '90gg' },
    { key: 'q', label: 'Trimestre' },
    { key: 'y', label: 'Anno' },
    { key: 'custom', label: 'Personalizzato' },
  ];
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
      {options.map(o => (
        <button
          key={o.key}
          type="button"
          onClick={() => onChange(o.key, customFrom, customTo)}
          style={{
            background: range === o.key ? 'var(--ink, #111)' : 'var(--surface, #fff)',
            color: range === o.key ? '#fff' : 'var(--ink-2, #444)',
            border: '1px solid var(--line, #e5e5e5)',
            borderRadius: 999,
            padding: '6px 12px',
            fontSize: 12.5,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          {o.label}
        </button>
      ))}
      {range === 'custom' && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12 }}>
          <input
            type="date"
            value={customFrom || ''}
            onChange={(e) => onChange('custom', e.target.value || null, customTo)}
            style={{ padding: 5, border: '1px solid var(--line, #e5e5e5)', borderRadius: 6, fontSize: 12 }}
            aria-label="Data inizio"
          />
          <span>→</span>
          <input
            type="date"
            value={customTo || ''}
            onChange={(e) => onChange('custom', customFrom, e.target.value || null)}
            style={{ padding: 5, border: '1px solid var(--line, #e5e5e5)', borderRadius: 6, fontSize: 12 }}
            aria-label="Data fine"
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card layout
// ---------------------------------------------------------------------------

function Card({ title, sub, children, action }: {
  title: string;
  sub?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <section
      style={{
        background: 'var(--surface, #fff)',
        border: '1px solid var(--line, #e5e5e5)',
        borderRadius: 14,
        padding: '20px 22px',
        marginBottom: 18,
      }}
    >
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: 'var(--ink, #111)' }}>{title}</h2>
          {sub && <div style={{ fontSize: 12, color: 'var(--ink-3, #666)', marginTop: 4 }}>{sub}</div>}
        </div>
        {action}
      </header>
      {children}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Donut chart (SVG inline) per Weight target reached
// ---------------------------------------------------------------------------

function Donut({ reached, total }: { reached: number; total: number }) {
  const pct = total > 0 ? Math.round((reached / total) * 100) : 0;
  const R = 52;
  const C = 2 * Math.PI * R;
  const offset = C * (1 - pct / 100);

  return (
    <svg width={140} height={140} viewBox="0 0 140 140" role="img" aria-label={`${pct}% target raggiunti`}>
      <circle cx={70} cy={70} r={R} fill="none" stroke="var(--line, #e5e5e5)" strokeWidth={14} />
      <circle
        cx={70}
        cy={70}
        r={R}
        fill="none"
        stroke="#0b8a3e"
        strokeWidth={14}
        strokeDasharray={C}
        strokeDashoffset={offset}
        transform="rotate(-90 70 70)"
        strokeLinecap="round"
      />
      <text x={70} y={68} textAnchor="middle" fontSize={26} fontWeight={800} fill="var(--ink, #111)">
        {pct}%
      </text>
      <text x={70} y={88} textAnchor="middle" fontSize={11} fontFamily="var(--mono, monospace)" fill="var(--mute, #888)">
        {reached}/{total}
      </text>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Bucket drill-down modal
// ---------------------------------------------------------------------------

function BucketModal({
  label,
  range,
  customFrom,
  customTo,
  onClose,
  onMessagePatient,
}: {
  label: string;
  range: AnalyticsRange;
  customFrom: string | null;
  customTo: string | null;
  onClose: () => void;
  onMessagePatient: (id: string) => void;
}) {
  const { data, isLoading } = useAnalyticsAdherenceBucket(label, range, customFrom, customTo);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
      role="dialog"
      aria-modal
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface, #fff)', borderRadius: 14, padding: 24,
          maxWidth: 640, width: '100%', maxHeight: '85vh', overflow: 'auto',
        }}
      >
        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Bucket {label}% — Pazienti</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Chiudi"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4 }}
          >
            <X size={20} />
          </button>
        </header>
        {isLoading && <div style={{ color: 'var(--mute, #888)' }}>Caricamento…</div>}
        {data && data.patients.length === 0 && (
          <div style={{ color: 'var(--mute, #888)', textAlign: 'center', padding: 24 }}>
            Nessun paziente in questo bucket.
          </div>
        )}
        {data && data.patients.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--surface-2, #f7f7f7)' }}>
                <th style={{ padding: '8px 10px', textAlign: 'left' }}>Paziente</th>
                <th style={{ padding: '8px 10px', textAlign: 'right' }}>Aderenza</th>
                <th style={{ padding: '8px 10px', textAlign: 'right' }}>Checks/Att.</th>
                <th style={{ padding: '8px 10px', textAlign: 'center', width: 90 }}>Azione</th>
              </tr>
            </thead>
            <tbody>
              {data.patients.map(p => (
                <tr key={p.patient_id} style={{ borderTop: '1px solid var(--line, #e5e5e5)' }}>
                  <td style={{ padding: '10px' }}>
                    <div style={{ fontWeight: 500 }}>{p.patient_name}</div>
                    <div style={{ fontSize: 10.5, color: 'var(--mute, #888)', fontFamily: 'var(--mono, monospace)' }}>
                      {p.human_id || '—'}
                    </div>
                  </td>
                  <td style={{ padding: '10px', textAlign: 'right', fontWeight: 600 }}>{p.adherence_pct}%</td>
                  <td style={{ padding: '10px', textAlign: 'right', fontFamily: 'var(--mono, monospace)' }}>
                    {p.meal_checks_in_range}/{p.expected_in_range}
                  </td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>
                    <button
                      type="button"
                      onClick={() => onMessagePatient(p.patient_id)}
                      style={{
                        background: 'var(--green-soft, #e8f5ee)',
                        color: 'var(--green-deep, #0b8a3e)',
                        border: 'none', borderRadius: 6, padding: '5px 10px',
                        fontSize: 11.5, fontWeight: 500, cursor: 'pointer',
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                      }}
                      aria-label={`Invia messaggio a ${p.patient_name}`}
                    >
                      <MessageSquare size={11} strokeWidth={2} /> Msg
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Funnel step drill-down modal
// ---------------------------------------------------------------------------

function FunnelStepModal({ step, onClose }: { step: FunnelStep; onClose: () => void }) {
  const { data, isLoading } = useAnalyticsFunnelStep(step);
  const labels: Record<FunnelStep, string> = {
    invited: 'Invitati',
    onboarded: 'Onboarded',
    first_plan: 'Primo piano',
    active_30d: 'Attivi 30gg',
  };
  const suggestions: Record<FunnelStep, string> = {
    invited: 'Suggerimento: invia magic-link di onboarding o messaggio WhatsApp di benvenuto.',
    onboarded: 'Suggerimento: invita il paziente a un primo appuntamento; carica un piano alimentare.',
    first_plan: 'Suggerimento: il paziente ha un piano ma non lo segue. Programma un follow-up.',
    active_30d: 'Pazienti pienamente attivi — nessuna azione richiesta.',
  };
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
      role="dialog"
      aria-modal
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface, #fff)', borderRadius: 14, padding: 24,
          maxWidth: 600, width: '100%', maxHeight: '85vh', overflow: 'auto',
        }}
      >
        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Funnel — {labels[step]}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Chiudi"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4 }}
          >
            <X size={20} />
          </button>
        </header>
        <div style={{
          background: 'var(--surface-2, #f7f7f7)',
          padding: '10px 14px', borderRadius: 8,
          fontSize: 12, color: 'var(--ink-2, #444)', marginBottom: 16,
        }}>
          {suggestions[step]}
        </div>
        {isLoading && <div style={{ color: 'var(--mute, #888)' }}>Caricamento…</div>}
        {data && data.stuck_list.length === 0 && (
          <div style={{ color: 'var(--mute, #888)', textAlign: 'center', padding: 24 }}>
            Nessun paziente bloccato a questo step.
          </div>
        )}
        {data && data.stuck_list.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--surface-2, #f7f7f7)' }}>
                <th style={{ padding: '8px 10px', textAlign: 'left' }}>Paziente</th>
                <th style={{ padding: '8px 10px', textAlign: 'right' }}>Giorni</th>
              </tr>
            </thead>
            <tbody>
              {data.stuck_list.map(p => (
                <tr key={p.patient_id} style={{ borderTop: '1px solid var(--line, #e5e5e5)' }}>
                  <td style={{ padding: '10px' }}>
                    <div style={{ fontWeight: 500 }}>{p.name}</div>
                    <div style={{ fontSize: 10.5, color: 'var(--mute, #888)', fontFamily: 'var(--mono, monospace)' }}>
                      {p.human_id || '—'}
                    </div>
                  </td>
                  <td style={{ padding: '10px', textAlign: 'right', fontFamily: 'var(--mono, monospace)' }}>
                    {p.days_at_step}g
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Scheduled reports settings
// ---------------------------------------------------------------------------

function ScheduledReportsSection() {
  const { data } = useAnalyticsScheduledReports();
  const createMut = useCreateAnalyticsScheduledReport();
  const deleteMut = useDeleteAnalyticsScheduledReport();
  const [cadence, setCadence] = useState<'weekly' | 'monthly'>('weekly');
  const [format, setFormat] = useState<'html' | 'csv' | 'json'>('html');
  const [email, setEmail] = useState('');

  const list = data?.reports || [];

  const handleAdd = () => {
    void (async () => {
      try {
        await createMut.mutateAsync({ cadence, format, email: email.trim() || undefined });
        setEmail('');
      } catch (e) {
        console.error(e);
      }
    })();
  };

  const handleDelete = (id: string) => {
    void (async () => {
      if (!confirm('Eliminare il report scheduled?')) return;
      try {
        await deleteMut.mutateAsync(id);
      } catch (e) {
        console.error(e);
      }
    })();
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        <select
          value={cadence}
          onChange={(e) => setCadence(e.target.value as 'weekly' | 'monthly')}
          aria-label="Frequenza"
          style={{ padding: 6, fontSize: 12, border: '1px solid var(--line, #e5e5e5)', borderRadius: 6 }}
        >
          <option value="weekly">Settimanale (Lun)</option>
          <option value="monthly">Mensile (giorno 1)</option>
        </select>
        <select
          value={format}
          onChange={(e) => setFormat(e.target.value as 'html' | 'csv' | 'json')}
          aria-label="Formato"
          style={{ padding: 6, fontSize: 12, border: '1px solid var(--line, #e5e5e5)', borderRadius: 6 }}
        >
          <option value="html">HTML</option>
          <option value="csv">CSV</option>
          <option value="json">JSON</option>
        </select>
        <input
          type="email"
          placeholder="Email destinatario (vuoto = tua email)"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-label="Email destinatario"
          style={{ padding: 6, fontSize: 12, border: '1px solid var(--line, #e5e5e5)', borderRadius: 6, flex: 1, minWidth: 200 }}
        />
        <button
          type="button"
          onClick={handleAdd}
          disabled={createMut.isPending}
          style={{
            background: 'var(--ink, #111)', color: '#fff', border: 'none',
            padding: '6px 14px', fontSize: 12, borderRadius: 6, cursor: 'pointer',
            opacity: createMut.isPending ? 0.6 : 1,
          }}
        >
          {createMut.isPending ? 'Salvataggio…' : 'Aggiungi'}
        </button>
      </div>
      {list.length === 0 ? (
        <div style={{ color: 'var(--mute, #888)', fontSize: 12 }}>
          Nessun report scheduled. Configura sopra per ricevere report periodici via email.
        </div>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {list.map(r => (
            <li
              key={r.id}
              style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '8px 10px', borderRadius: 6,
                background: 'var(--surface-2, #f7f7f7)', marginBottom: 6,
              }}
            >
              <div style={{ fontSize: 12 }}>
                <b>{r.cadence === 'weekly' ? 'Settimanale' : 'Mensile'}</b> · {r.format.toUpperCase()} → {r.email}
                {r.last_sent_at && (
                  <span style={{ color: 'var(--mute, #888)', marginLeft: 8 }}>
                    ultimo invio: {new Date(r.last_sent_at).toLocaleDateString('it-IT')}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => handleDelete(r.id)}
                style={{
                  background: 'transparent', border: 'none',
                  color: '#c0392b', cursor: 'pointer', fontSize: 12, padding: 4,
                }}
                aria-label="Elimina"
              >
                <X size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Saved views section
// ---------------------------------------------------------------------------

function SavedViewsSection({
  range, customFrom, customTo,
}: {
  range: AnalyticsRange;
  customFrom: string | null;
  customTo: string | null;
}) {
  const { data } = useAnalyticsSavedViews();
  const createMut = useCreateAnalyticsSavedView();
  const [name, setName] = useState('');

  const list = data?.views || [];

  const handleSave = () => {
    void (async () => {
      if (!name.trim()) return;
      try {
        await createMut.mutateAsync({
          name: name.trim(),
          config: { range, custom_from: customFrom, custom_to: customTo },
        });
        setName('');
      } catch (e) {
        console.error(e);
      }
    })();
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        <input
          type="text"
          placeholder="Nome vista (es. 'Q2 mensile')"
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-label="Nome vista"
          style={{ flex: 1, padding: 6, fontSize: 12, border: '1px solid var(--line, #e5e5e5)', borderRadius: 6 }}
        />
        <button
          type="button"
          onClick={handleSave}
          disabled={!name.trim() || createMut.isPending}
          style={{
            background: 'var(--ink, #111)', color: '#fff', border: 'none',
            padding: '6px 14px', fontSize: 12, borderRadius: 6, cursor: 'pointer',
            opacity: !name.trim() || createMut.isPending ? 0.5 : 1,
          }}
        >
          Salva vista corrente
        </button>
      </div>
      {list.length === 0 ? (
        <div style={{ color: 'var(--mute, #888)', fontSize: 12 }}>
          Nessuna vista salvata. Configura range + filtri sopra e salva con un nome.
        </div>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {list.map(v => (
            <li key={v.id} style={{
              padding: '6px 10px', background: 'var(--surface-2, #f7f7f7)',
              borderRadius: 6, marginBottom: 4, fontSize: 12,
            }}>
              <b>{v.name}</b>
              {v.is_default === 1 && <span style={{ color: 'var(--green-deep, #0b8a3e)', marginLeft: 6 }}>★ default</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function Analytics() {
  const [range, setRange] = useState<AnalyticsRange>('30d');
  const [customFrom, setCustomFrom] = useState<string | null>(null);
  const [customTo, setCustomTo] = useState<string | null>(null);
  const [bucketLabel, setBucketLabel] = useState<string | null>(null);
  const [funnelStepDetail, setFunnelStepDetail] = useState<FunnelStep | null>(null);
  const qc = useQueryClient();

  const overviewQ = useAnalyticsOverview(range, customFrom, customTo);
  const retentionQ = useAnalyticsRetention();
  const adherenceQ = useAnalyticsAdherence(range, customFrom, customTo);
  const weightQ = useAnalyticsWeightTarget();
  const funnelQ = useAnalyticsFunnel();

  const overview = overviewQ.data;
  const isLoading = overviewQ.isLoading || retentionQ.isLoading || adherenceQ.isLoading || weightQ.isLoading || funnelQ.isLoading;

  const handleRangeChange = (r: AnalyticsRange, from: string | null, to: string | null) => {
    setRange(r);
    setCustomFrom(from);
    setCustomTo(to);
  };

  const handleRefresh = () => {
    void qc.invalidateQueries({ queryKey: ['pro', 'analytics'] });
  };

  const handleExport = (format: 'csv' | 'json' | 'html') => {
    // Optimistic spinner via window.location: browser sceglie attachment/download
    window.location.href = exportAnalyticsUrl(format);
  };

  const handleMessagePatient = (patientId: string) => {
    // Naviga a /messaggi con paziente preselezionato
    window.location.href = `/messaggi?patient=${encodeURIComponent(patientId)}`;
  };

  const periodInfo = useMemo(() => {
    if (!overview) return null;
    return `${overview.range.from} → ${overview.range.to}`;
  }, [overview]);

  return (
    <>
      <ProTopBar
        breadcrumb="Studio Pro · Insight"
        title="Analytics"
      />

      {/* Range picker + refresh + saved views toggle */}
      <div
        style={{
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, marginBottom: 20, padding: '14px 18px',
          background: 'var(--surface, #fff)', border: '1px solid var(--line, #e5e5e5)', borderRadius: 12,
        }}
      >
        <div>
          <div style={{
            fontFamily: 'var(--mono, monospace)', fontSize: 10.5, fontWeight: 600,
            color: 'var(--mute, #888)', letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 6,
          }}>
            Periodo · Comparazione vs precedente · {rangeLabel(range, customFrom, customTo)}
          </div>
          <RangePicker
            range={range}
            customFrom={customFrom}
            customTo={customTo}
            onChange={handleRangeChange}
          />
          {periodInfo && (
            <div style={{ fontSize: 11, color: 'var(--mute, #888)', marginTop: 6, fontFamily: 'var(--mono, monospace)' }}>
              {periodInfo}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isLoading}
            style={{
              background: 'var(--surface, #fff)', border: '1px solid var(--line, #e5e5e5)', borderRadius: 8,
              padding: '7px 12px', fontSize: 12, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
            aria-label="Aggiorna dati"
          >
            <RefreshCw size={13} strokeWidth={2} className={isLoading ? 'spin' : ''} />
            Aggiorna
          </button>
          <button
            type="button"
            onClick={() => handleExport('csv')}
            style={{
              background: 'var(--green-deep, #0b8a3e)', color: '#fff', border: 'none', borderRadius: 8,
              padding: '7px 14px', fontSize: 12, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
            aria-label="Esporta CSV"
          >
            <Download size={13} strokeWidth={2} /> CSV
          </button>
          <button
            type="button"
            onClick={() => handleExport('json')}
            style={{
              background: 'var(--surface, #fff)', color: 'var(--ink, #111)', border: '1px solid var(--line, #e5e5e5)', borderRadius: 8,
              padding: '7px 12px', fontSize: 12, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
            aria-label="Esporta JSON"
          >
            <Download size={13} strokeWidth={2} /> JSON
          </button>
          <button
            type="button"
            onClick={() => handleExport('html')}
            style={{
              background: 'var(--surface, #fff)', color: 'var(--ink, #111)', border: '1px solid var(--line, #e5e5e5)', borderRadius: 8,
              padding: '7px 12px', fontSize: 12, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
            aria-label="Report HTML (PDF via stampa)"
          >
            <Download size={13} strokeWidth={2} /> PDF
          </button>
        </div>
      </div>

      {/* KPI grid */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 18,
      }}
      className="analytics-kpi-grid">
        <AnalyticsKPICard
          label="Pazienti totali"
          value={overview ? formatNumber(overview.patients_total) : '—'}
          deltaPct={overview?.patients_total_delta_pct ?? null}
          prevValue={overview ? formatNumber(overview.patients_total_prev) : ''}
          icon={<Users size={14} strokeWidth={2.2} />}
          iconBg="var(--green-soft, #e8f5ee)"
          iconColor="var(--green-deep, #0b8a3e)"
        />
        <AnalyticsKPICard
          label="Aderenza media"
          value={overview ? String(overview.adherence_avg) : '—'}
          unit="%"
          deltaPct={overview?.adherence_avg_delta_pct ?? null}
          prevValue={overview ? `${overview.adherence_avg_prev}%` : ''}
          icon={<ClipboardCheck size={14} strokeWidth={2.2} />}
          iconBg="#fff3d6"
          iconColor="#a87c00"
        />
        <AnalyticsKPICard
          label="Attivi nel periodo"
          value={overview ? formatNumber(overview.active_in_range) : '—'}
          deltaPct={overview?.active_in_range_delta_pct ?? null}
          prevValue={overview ? formatNumber(overview.active_in_range_prev) : ''}
          icon={<Activity size={14} strokeWidth={2.2} />}
          iconBg="rgba(74,144,226,0.12)"
          iconColor="#1d4ed8"
        />
        <AnalyticsKPICard
          label="Tasso conversione"
          value={overview ? `${Math.round(overview.conversion_rate * 100)}` : '—'}
          unit="%"
          deltaPct={overview?.conversion_rate_delta_pct ?? null}
          prevValue={overview ? `${Math.round(overview.conversion_rate_prev * 100)}%` : ''}
          icon={<TrendingUp size={14} strokeWidth={2.2} />}
          iconBg="rgba(155,89,182,0.12)"
          iconColor="#7c3aed"
        />
      </div>

      {/* Retention + Adherence row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginBottom: 0 }} className="analytics-row">
        <Card title="Retention coorti" sub="Pazienti retained a D7/D30/D90 dalla data di creazione">
          <RetentionHeatmap cohorts={retentionQ.data?.cohorts || []} />
        </Card>
        <Card title="Distribuzione aderenza" sub="Click su un bucket per drill-down">
          <AdherenceBars
            buckets={adherenceQ.data?.buckets || []}
            totalPatients={adherenceQ.data?.total_patients || 0}
            avgAdherence={adherenceQ.data?.avg_adherence || 0}
            onBucketClick={setBucketLabel}
          />
        </Card>
      </div>

      {/* Funnel + Weight target row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginBottom: 0 }} className="analytics-row">
        <Card title="Funnel conversione" sub="Click su uno step per il dettaglio dei pazienti bloccati">
          {funnelQ.data ? (
            <FunnelChart funnel={funnelQ.data} onStepClick={setFunnelStepDetail} />
          ) : (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--mute, #888)' }}>Caricamento…</div>
          )}
        </Card>
        <Card title="Target peso" sub="Obiettivi peso impostati e progresso">
          <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
            <Donut reached={weightQ.data?.reached || 0} total={weightQ.data?.total_with_goal || 0} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: 11, fontFamily: 'var(--mono, monospace)', fontWeight: 600,
                color: 'var(--mute, #888)', letterSpacing: 1, marginBottom: 8, textTransform: 'uppercase',
              }}>
                Top 5 in avvicinamento
              </div>
              {weightQ.data && weightQ.data.reaching.length > 0 ? (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {weightQ.data.reaching.slice(0, 5).map(p => (
                    <li key={p.patient_id} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '6px 0', borderBottom: '1px solid var(--line, #e5e5e5)', fontSize: 12,
                    }}>
                      <div>
                        <div style={{ fontWeight: 500 }}>{p.name}</div>
                        <div style={{
                          fontSize: 10.5, color: 'var(--mute, #888)', fontFamily: 'var(--mono, monospace)',
                        }}>
                          {p.current_kg.toFixed(1)} → target {p.target_kg.toFixed(1)}kg
                        </div>
                      </div>
                      <div style={{
                        fontWeight: 700, color: 'var(--green-deep, #0b8a3e)',
                        fontFamily: 'var(--mono, monospace)',
                      }}>
                        {p.progress_pct}%
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div style={{ color: 'var(--mute, #888)', fontSize: 12 }}>
                  Nessun goal peso impostato. Aggiungi target dal profilo paziente.
                </div>
              )}
            </div>
          </div>
          <div style={{
            marginTop: 16, padding: '8px 12px', background: 'var(--surface-2, #f7f7f7)', borderRadius: 6,
            fontSize: 11, color: 'var(--ink-3, #666)',
          }}>
            Obiettivi gestiti via tabella <code>patient_goal</code>. Goal raggiunto quando |peso corrente - target| ≤ 0.5kg.
          </div>
        </Card>
      </div>

      {/* Saved views + scheduled reports row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginBottom: 18 }} className="analytics-row">
        <Card title="Viste salvate" sub="Salva configurazioni filtri per accesso rapido" action={<Settings size={14} />}>
          <SavedViewsSection range={range} customFrom={customFrom} customTo={customTo} />
        </Card>
        <Card title="Report scheduled" sub="Email automatiche periodiche con metriche aggiornate" action={<Mail size={14} />}>
          <ScheduledReportsSection />
        </Card>
      </div>

      {/* Drill-down modals */}
      {bucketLabel && (
        <BucketModal
          label={bucketLabel}
          range={range}
          customFrom={customFrom}
          customTo={customTo}
          onClose={() => setBucketLabel(null)}
          onMessagePatient={(id) => {
            setBucketLabel(null);
            handleMessagePatient(id);
          }}
        />
      )}
      {funnelStepDetail && (
        <FunnelStepModal step={funnelStepDetail} onClose={() => setFunnelStepDetail(null)} />
      )}

      {/* Inline responsive CSS via style tag */}
      <style>{`
        @media (max-width: 900px) {
          .analytics-kpi-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .analytics-row { grid-template-columns: 1fr !important; }
        }
        @keyframes analyticsSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin { animation: analyticsSpin 1s linear infinite; }
      `}</style>
    </>
  );
}
